<script setup>
import { computed } from 'vue';
import { Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { CHART_COLORS, GAUGE_SIZE_CONFIG } from '../../constants';

const props = defineProps({
  value: {
    type: Number,
    required: true,
    validator: v => v >= 0 && v <= 100,
  },
  size: {
    type: String,
    default: 'md',
    validator: v => ['sm', 'md', 'lg'].includes(v),
  },
  showValue: {
    type: Boolean,
    default: true,
  },
  thresholds: {
    type: Object,
    default: () => ({ good: 80, warning: 70 }),
  },
  label: {
    type: String,
    default: '',
  },
});

ChartJS.register(ArcElement, Tooltip);

const currentSize = computed(() => GAUGE_SIZE_CONFIG[props.size]);

const gaugeColor = computed(() => {
  if (props.value >= props.thresholds.good) return CHART_COLORS.good;
  if (props.value >= props.thresholds.warning) return CHART_COLORS.warning;
  return CHART_COLORS.danger;
});

const chartData = computed(() => ({
  datasets: [
    {
      data: [props.value, 100 - props.value],
      backgroundColor: [gaugeColor.value, CHART_COLORS.background],
      borderWidth: 0,
      cutout: '75%',
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  circumference: 180,
  rotation: -90,
  plugins: {
    tooltip: {
      enabled: false,
    },
  },
}));
</script>

<template>
  <div class="flex flex-col items-center">
    <div
      class="relative"
      :style="{
        width: `${currentSize.width}px`,
        height: `${currentSize.height}px`,
      }"
    >
      <Doughnut :data="chartData" :options="chartOptions" />
      <div
        v-if="showValue"
        class="absolute inset-0 flex items-end justify-center pb-1"
      >
        <span
          class="font-semibold text-n-slate-12"
          :class="currentSize.fontSize"
        >
          {{ value }}%
        </span>
      </div>
    </div>
    <span v-if="label" class="mt-2 text-sm text-n-slate-11">
      {{ label }}
    </span>
  </div>
</template>
